import React from 'react';
import { FaChevronRight, FaRegCommentDots, FaEdit, FaTrash, FaEllipsisV } from 'react-icons/fa';
import RoleBadge from './RoleBadge';

interface ForumCategoryRowProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    stats: {
        topics: number;
    };
    latestPost?: {
        id: string;
        title: string;
        author: {
            name: string;
            avatar: string | null;
            role?: string;
        };
        time: string;
    } | null;
    onClick: () => void;
    onLatestPostClick?: (id: string) => void;
    color: string;
    onEdit?: () => void;
    onDelete?: () => void;
}

const ForumCategoryRow: React.FC<ForumCategoryRowProps> = ({
    icon,
    title,
    description,
    stats,
    latestPost,
    onClick,
    onLatestPostClick,
    color,
    onEdit,
    onDelete
}) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            onClick={onClick}
            className="group relative bg-white dark:bg-gray-800 p-3 sm:p-5 flex flex-row gap-3 sm:gap-6 items-center border-b last:border-b-0 border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all cursor-pointer"
        >
            {/* Icon Column */}
            <div className={`shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl ${color} bg-opacity-10 dark:bg-opacity-20 flex items-center justify-center text-xl sm:text-2xl shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                {icon}
            </div>

            {/* Main Info */}
            <div className="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-12 gap-2 sm:gap-4 w-full">

                {/* Title & Desc (7 cols) */}
                <div className="lg:col-span-7 pr-4 sm:pr-0">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-0.5 sm:mb-1 truncate pr-8 sm:pr-0">
                        {title}
                    </h3>
                    <p className="hidden sm:block text-sm text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                        {description}
                    </p>
                    <p className="sm:hidden text-xs text-gray-400 dark:text-gray-500 truncate">
                        {description}
                    </p>

                    {/* Mobile Stats (Visible only on small screens) */}
                    <div className="flex items-center gap-3 mt-1.5 sm:mt-3 lg:hidden text-[10px] sm:text-xs font-medium text-gray-400">
                        <span className="flex items-center gap-1">
                            <FaRegCommentDots /> {stats.topics} bài
                        </span>
                    </div>
                </div>

                {/* Stats (2 cols) - Hidden on mobile */}
                <div className="hidden lg:flex lg:col-span-2 flex-col justify-center items-center text-center border-l border-gray-100 dark:border-gray-700">
                    <span className="text-xl font-bold text-gray-900 dark:text-white">{stats.topics}</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Bài viết</span>
                </div>

                {/* Latest Post (3 cols) */}
                <div className="hidden lg:flex lg:col-span-3 items-center lg:border-l border-gray-100 dark:border-gray-700 lg:pl-6">
                    {latestPost ? (
                        <div
                            className="flex gap-3 items-center w-full cursor-pointer group/post p-1 -m-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onLatestPostClick?.(latestPost.id);
                            }}
                        >
                            <div className="shrink-0 relative">
                                {latestPost.author.avatar ? (
                                    <img
                                        src={latestPost.author.avatar}
                                        alt={latestPost.author.name}
                                        className="w-10 h-10 rounded-full object-cover ring-2 ring-white dark:ring-gray-800"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center text-sm font-bold text-gray-600 dark:text-gray-300 ring-2 ring-white dark:ring-gray-800">
                                        {latestPost.author.name.substring(0, 1).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold text-gray-900 dark:text-white truncate group-hover:text-blue-600 transition-colors">
                                    {latestPost.title}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-0.5">
                                    <span>{new Date(latestPost.time).toLocaleDateString('vi-VN')}</span>
                                    <span>•</span>
                                    <span className="font-medium text-gray-700 dark:text-gray-300 truncate">
                                        {latestPost.author.name}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm text-gray-400 italic">
                            Chưa có bài viết
                        </div>
                    )}
                </div>
            </div>

            {/* Admin Actions Dropdown (3 dots) */}
            {(onEdit || onDelete) && (
                <div className="absolute top-1/2 -translate-y-1/2 right-1 sm:right-2 z-20" ref={menuRef}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(!showMenu);
                        }}
                        className={`p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors ${showMenu ? 'opacity-100 bg-gray-100 dark:bg-gray-700' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
                    >
                        <FaEllipsisV />
                    </button>

                    {showMenu && (
                        <div className="absolute right-0 top-full mt-1 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-100 dark:border-gray-700 py-1 overflow-hidden animate-fadeIn origin-top-right z-[1000]">
                            {onEdit && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMenu(false);
                                        onEdit();
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                    <FaEdit className="text-gray-400" /> Sửa
                                </button>
                            )}
                            {onDelete && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMenu(false);
                                        onDelete();
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2"
                                >
                                    <FaTrash className="text-red-500" /> Xóa
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ForumCategoryRow;
